import { useState, ReactNode, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { layoutToScreen } from "../../utils/uiZoom";
import {
  Box,
  IconButton,
  Typography,
  Button,
  Badge,
  Tooltip,
} from "@mui/material";
import Icon from "@mui/material/Icon";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import FilterListIcon from "@mui/icons-material/FilterList";

import { useLayerControlSide } from "./layerControlSide";

// Width the round pill grows to on hover to show its title.
const EXPANDED_PILL_WIDTH = 160;
// Space between the expanded pill and its panel; covers the pill's 1.05 hover scale.
const PANEL_GAP = 12;

interface LayerControlItemProps {
  title: string;
  icon: string | ReactNode;
  visible: boolean;
  onToggleVisibility: () => void;
  enabled?: boolean;
  /** Shown as a tooltip on the toggle while `enabled` is false. */
  disabledReason?: string;
  filterCount?: number;
  onFilterClick?: () => void;
  filterDisabled?: boolean;
  filterDisabledTooltip?: string;
  drawingContent?: ReactNode;
  drawingContentLabel?: string;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  /**
   * When true, the floating panel stays open regardless of hover state.
   * Used while the user is interacting with controls inside the panel
   * (e.g. a colour picker) that may briefly send the mouse outside the panel.
   */
  keepOpen?: boolean;
  /**
   * Fired when the user clicks outside the panel and the main button while
   * the panel is open. Use this to release `keepOpen` from the parent so the
   * panel can collapse.
   *
   * Careful: anything rendered through a portal — a Dialog, a Menu, a Popover —
   * is outside the panel in the DOM, so clicking *inside* one fires this. Do
   * not close modals from here; let them close via their own onClose and just
   * let `keepOpen` follow their open state.
   */
  onOutsideClick?: () => void;
  /**
   * Fired as this item's hover panel opens and closes. A group that reveals
   * its items on hover uses it to stay open while one of their panels is in
   * use, since the panel is a portal outside the group's own box.
   */
  onPanelOpenChange?: (open: boolean) => void;
}

const LayerControlItem: React.FC<LayerControlItemProps> = ({
  title,
  icon,
  visible,
  onToggleVisibility,
  enabled = true,
  disabledReason,
  filterCount = 0,
  onFilterClick,
  filterDisabled = false,
  filterDisabledTooltip = "No data loaded — add features to enable filters",
  drawingContent,
  drawingContentLabel = "Drawing Tools",
  expanded = false,
  onExpandedChange,
  keepOpen = false,
  onOutsideClick,
  onPanelOpenChange,
}) => {
  const side = useLayerControlSide();
  const [hovered, setHovered] = useState(false);
  const isExpanded = expanded || hovered || keepOpen;
  const panelOpen = hovered || keepOpen;

  useEffect(() => {
    onPanelOpenChange?.(panelOpen);
  }, [panelOpen, onPanelOpenChange]);
  const [buttonY, setButtonY] = useState(0);
  // Distance from the viewport's right edge to just left of the expanded pill,
  // so the panel opens beside it wherever the pill column sits (viewport edge
  // on the project page, the map pane's edge in the listing's split view).
  const [panelRight, setPanelRight] = useState(220);
  const [panelLeft, setPanelLeft] = useState(0);
  const buttonRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!panelOpen || !onOutsideClick) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (panelRef.current?.contains(target)) return;
      if (buttonRef.current?.contains(target)) return;
      onOutsideClick();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [panelOpen, onOutsideClick]);

  useEffect(() => {
    if (buttonRef.current && panelOpen) {
      const rect = buttonRef.current.getBoundingClientRect();
      const y = rect.top + rect.height / 2;
      setButtonY(y);
      // The pill is still collapsed here and grows over 0.3s away from the
      // column's edge, so measure from that edge out to its expanded width.
      // rect is in screen px; the pill width and gap are layout px.
      const pillWidth = layoutToScreen(EXPANDED_PILL_WIDTH);
      const gap = layoutToScreen(PANEL_GAP);
      setPanelRight(Math.max(8, Math.round(window.innerWidth - (rect.right - pillWidth) + gap)));
      setPanelLeft(Math.round(rect.left + pillWidth + gap));
    }
  }, [panelOpen, title]);

  const handleMouseEnter = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setHovered(true);
    if (onExpandedChange) {
      onExpandedChange(true);
    }
  };

  const handleMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      setHovered(false);
    }, 200);
  };

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  return (
    <>
      {/* Expanded hover panel with eye toggle and expansion button */}
      {panelOpen && buttonY > 0 && createPortal(
        <Box
          ref={panelRef}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          sx={{
            position: "fixed",
            // Portaled to <body>, so zoom ourselves and express the screen-px
            // offsets in our own zoomed coordinates (index.css --ui-zoom).
            zoom: "var(--ui-zoom)",
            ...(side === "left"
              ? { left: `calc(${panelLeft}px / var(--ui-zoom))` }
              : { right: `calc(${panelRight}px / var(--ui-zoom))` }),
            top: `calc(${buttonY}px / var(--ui-zoom))`,
            transform: "translateY(-50%)",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: 0,
            backgroundColor: "rgba(30, 41, 59, 0.95)",
            backdropFilter: "blur(8px)",
            borderRadius: 2,
            padding: "12px 16px",
            border: "2px solid rgba(148, 163, 184, 0.2)",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.5)",
            minWidth: 280,
            maxWidth: 320,
            maxHeight: "calc(var(--vh100) * 0.8)",
            overflowY: "auto",
            zIndex: 9999999,
            opacity: panelOpen ? 1 : 0,
            transition: "opacity 0.2s ease-in-out, transform 0.2s ease-in-out",
            animation: "slideIn 0.2s ease-out",
            "@keyframes slideIn": {
              from: {
                opacity: 0,
                transform: side === "left" ? "translateY(-50%) translateX(-10px)" : "translateY(-50%) translateX(10px)",
              },
              to: {
                opacity: 1,
                transform: "translateY(-50%) translateX(0)",
              },
            },
          }}
        >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", mb: 0.5 }}>
          <Typography
            variant="caption"
            sx={{
              color: "#f8fafc",
              fontWeight: 600,
              fontSize: "0.875rem",
            }}
          >
            {title}
          </Typography>
          
          <Tooltip title={!enabled && disabledReason ? disabledReason : ""} placement={side === "left" ? "right" : "left"} arrow>
            <span>
              <IconButton
                size="small"
                onClick={onToggleVisibility}
                disabled={!enabled}
                sx={{
                  color: visible ? "#00d4ad" : "#64748b",
                  padding: "4px",
                  "&:hover": {
                    backgroundColor: "rgba(0, 212, 173, 0.1)",
                  },
                }}
              >
                {visible ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        {!enabled && disabledReason && (
          <Typography variant="caption" sx={{ color: "#94a3b8", display: "block", mb: 0.5 }}>
            {disabledReason}
          </Typography>
        )}

        {onFilterClick && (
          <Box sx={{ 
            width: "100%", 
            mt: 1,
            mb: drawingContent ? 1.5 : 0,
          }}>
            <Tooltip
              title={filterDisabled ? filterDisabledTooltip : ""}
              placement={side === "left" ? "right" : "left"}
              arrow
            >
              <span>
                <Button
                  size="small"
                  disabled={filterDisabled}
                  startIcon={<FilterListIcon fontSize="small" />}
                  onClick={onFilterClick}
                  sx={{
                    color: filterDisabled ? "#475569" : filterCount > 0 ? "#00d4ad" : "#94a3b8",
                    justifyContent: "flex-start",
                    textTransform: "none",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    "&:hover": {
                      backgroundColor: filterDisabled ? "transparent" : "rgba(0, 212, 173, 0.1)",
                    },
                    "&.Mui-disabled": {
                      color: "#475569",
                    },
                  }}
                >
                  {filterDisabled
                    ? "No data to filter"
                    : filterCount > 0
                    ? `${filterCount} Filter${filterCount > 1 ? 's' : ''} Active`
                    : "Add Filters"}
                </Button>
              </span>
            </Tooltip>
          </Box>
        )}

        {drawingContent && (
          <Box sx={{ 
            width: "100%", 
            mt: onFilterClick ? 0 : 1,
            pt: onFilterClick ? 1.5 : 0,
            borderTop: onFilterClick ? "1px solid rgba(148, 163, 184, 0.2)" : "none"
          }}>
            <Typography
              variant="caption"
              sx={{
                color: "#94a3b8",
                fontWeight: 600,
                textTransform: "uppercase",
                fontSize: "0.65rem",
                mb: 0.75,
                display: "block",
              }}
            >
              {drawingContentLabel}
            </Typography>
            {drawingContent}
          </Box>
        )}
        </Box>,
        document.body
      )}

      <Box
        ref={buttonRef}
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: side === "left" ? "flex-start" : "flex-end",
          minHeight: 60,
          width: "100%",
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* Main button - expands on hover to show title */}
        <Badge
          badgeContent={filterCount > 0 ? filterCount : 0}
          invisible={!filterCount}
          sx={{
            "& .MuiBadge-badge": {
              backgroundColor: "#f97316",
              color: "#fff",
              fontWeight: 700,
              fontSize: "0.65rem",
              minWidth: 18,
              height: 18,
              top: 6,
              right: 6,
            },
          }}
        >
        <Box
        onClick={enabled ? onToggleVisibility : undefined}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: isExpanded ? 1 : 0,
          width: isExpanded ? EXPANDED_PILL_WIDTH : 46,
          minWidth: 46,
          height: 46,
          paddingX: isExpanded ? 2 : 0,
          paddingRight: isExpanded ? 2.5 : 0,
          borderRadius: isExpanded ? "23px" : "50%",
          backgroundColor: visible ? "#00d4ad" : "rgba(0, 212, 173, 0.3)",
          color: "#fff",
          cursor: enabled ? "pointer" : "default",
          transition: "all 0.3s ease-in-out",
          border: "2px solid rgba(148, 163, 184, 0.2)",
          "&:hover": {
            backgroundColor: "#faad17",
            transform: "scale(1.05)",
          },
          overflow: "hidden",
          whiteSpace: "nowrap",
        }}
      >
        {typeof icon === "string" ? <Icon>{icon}</Icon> : icon}
        {isExpanded && (
          <Typography
            sx={{
              fontSize: "0.875rem",
              fontWeight: 600,
              color: "#fff",
              animation: "fadeIn 0.3s ease-in",
              "@keyframes fadeIn": {
                from: { opacity: 0 },
                to: { opacity: 1 },
              },
            }}
          >
            {title}
          </Typography>
        )}
        </Box>
        </Badge>
      </Box>
    </>
  );
};

export default LayerControlItem;
